/** Columnas user_lots (storage: column) — top-level en createuserlot. */
export const UF_ATTRIBUTE_COLUMN_KEYS = new Set([
  'total_surface',
  'covered_surface',
  'open_surface',
  'rooms',
  'bedrooms',
  'full_bathrooms',
  'parking_lots',
  'unit_floor',
  'year_built',
  'has_water',
  'has_gas',
  'has_electricity',
  'has_sewage',
]);

export const UF_WIZARD_STEP = {
  BUILDING_TYPE: 1,
  LISTING: 2,
  ATTRIBUTES: 3,
};

export const UF_BUILDING_TYPES = [
  { id: 'departamento', label: 'Departamento' },
  { id: 'casa', label: 'Casa' },
  { id: 'ph', label: 'PH' },
  { id: 'comercio', label: 'Comercio' },
];

export const UF_BUILDING_TYPES_FALLBACK = UF_BUILDING_TYPES;

const BUILDING_TYPE_ALIASES = {
  depto: 'departamento',
  department: 'departamento',
  apartment: 'departamento',
  apartamento: 'departamento',
  house: 'casa',
  vivienda: 'casa',
  local: 'comercio',
  comercial: 'comercio',
  oficina: 'comercio',
  office: 'comercio',
  commercial: 'comercio',
};

export function normalizeBuildingType(raw) {
  if (raw === null || raw === undefined) return '';
  const key = String(raw).trim().toLowerCase();
  if (!key) return '';
  if (UF_BUILDING_TYPES.some((t) => t.id === key)) return key;
  return BUILDING_TYPE_ALIASES[key] || '';
}

export function sortSchemaGroups(groups = []) {
  return [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function flattenSchemaFields(schema) {
  const fields = [];
  sortSchemaGroups(schema?.groups || []).forEach((group) => {
    (group.fields || []).forEach((field) => fields.push(field));
  });
  return fields;
}

export function resolveBuildingTypes(apiResponse) {
  const types = apiResponse?.data?.building_types;
  if (Array.isArray(types) && types.length > 0) {
    return types.map((row) => ({
      id: normalizeBuildingType(row.id) || row.id,
      label: row.label || row.id,
    })).filter((row) => row.id);
  }
  return UF_BUILDING_TYPES_FALLBACK;
}

export function resolveInitialWizardStep({ isEditing, buildingType }) {
  if (!isEditing) return UF_WIZARD_STEP.BUILDING_TYPE;
  return normalizeBuildingType(buildingType)
    ? UF_WIZARD_STEP.LISTING
    : UF_WIZARD_STEP.BUILDING_TYPE;
}

export function extractApiErrorMessage(errorLike, fallback = 'Ocurrió un error') {
  if (!errorLike) return fallback;
  if (typeof errorLike === 'string') return errorLike;
  if (typeof errorLike.error === 'string') return errorLike.error;
  if (typeof errorLike.message === 'string') return errorLike.message;
  return fallback;
}

export function clearAttributeFormValues(form = {}) {
  const next = { ...form, attributes: {} };
  UF_ATTRIBUTE_COLUMN_KEYS.forEach((key) => {
    if (!(key in next)) return;
    next[key] = typeof form[key] === 'boolean' ? false : '';
  });
  return next;
}

export function mergeDetailIntoAttributeForm(detail = {}, schema) {
  const patch = {
    attributes: { ...(detail.attributes && typeof detail.attributes === 'object' ? detail.attributes : {}) },
  };

  flattenSchemaFields(schema).forEach((field) => {
    if (field.storage === 'column') {
      const value = detail[field.id];
      if (value !== null && value !== undefined && value !== '') {
        patch[field.id] = value;
      }
      return;
    }
    const jsonValue = detail.attributes?.[field.id];
    if (jsonValue !== null && jsonValue !== undefined) {
      patch.attributes[field.id] = jsonValue;
    }
  });

  return patch;
}

function coerceColumnValue(rawValue, fieldType) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return null;
  }
  if (fieldType === 'boolean' || typeof rawValue === 'boolean') {
    return Boolean(rawValue);
  }
  if (fieldType === 'integer') {
    const parsed = parseInt(rawValue, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (fieldType === 'number') {
    const parsed = parseFloat(rawValue);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (fieldType === 'string') {
    const trimmed = String(rawValue).trim();
    return trimmed === '' ? null : trimmed;
  }
  return rawValue;
}

function buildAttributesPayloadFromValues(buildingType, attributeValues = {}, schemaFields = []) {
  if (!buildingType) return {};

  const fieldTypeById = {};
  schemaFields.forEach((field) => {
    fieldTypeById[field.id] = field.type;
  });

  const body = { building_type: buildingType };
  const attributes = {};

  Object.entries(attributeValues).forEach(([key, rawValue]) => {
    const fieldType = fieldTypeById[key];
    const isColumn = UF_ATTRIBUTE_COLUMN_KEYS.has(key)
      || schemaFields.find((field) => field.id === key)?.storage === 'column';

    // String JSON attrs: empty must be sent so backend can clear the JSONB key.
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      if (!isColumn && fieldType === 'string') {
        attributes[key] = '';
      }
      return;
    }

    if (isColumn) {
      body[key] = coerceColumnValue(rawValue, fieldType);
      return;
    }

    if (fieldType === 'boolean' && rawValue === false) {
      return;
    }

    attributes[key] = rawValue;
  });

  if (Object.keys(attributes).length > 0) {
    body.attributes = attributes;
  }

  return body;
}

/**
 * Arma atributos UF para POST /createuserlot.
 * Soporta (form, schema) o (buildingType, values, fields).
 */
export function buildAttributesPayload(formOrType, schemaOrValues, maybeFields) {
  if (formOrType && typeof formOrType === 'object' && schemaOrValues?.groups) {
    const form = formOrType;
    const schema = schemaOrValues;
    const buildingType = normalizeBuildingType(form.building_type);
    if (!buildingType) return {};

    const fields = flattenSchemaFields(schema);
    const values = {};

    fields.forEach((field) => {
      if (field.storage === 'column') {
        if (form[field.id] !== undefined && form[field.id] !== '') {
          values[field.id] = form[field.id];
        }
      } else if (form.attributes?.[field.id] !== undefined) {
        values[field.id] = form.attributes[field.id];
      }
    });

    return buildAttributesPayloadFromValues(buildingType, values, fields);
  }

  return buildAttributesPayloadFromValues(
    formOrType,
    schemaOrValues || {},
    maybeFields || [],
  );
}

export function formatAttributeSummary(buildingType, form = {}) {
  const typeLabel = UF_BUILDING_TYPES.find((t) => t.id === normalizeBuildingType(buildingType))?.label;
  const parts = [];
  if (typeLabel) parts.push(typeLabel);
  if (form.bedrooms != null && form.bedrooms !== '') parts.push(`${form.bedrooms} dorm.`);
  if (form.rooms != null && form.rooms !== '') parts.push(`${form.rooms} amb.`);
  if (form.covered_surface != null && form.covered_surface !== '') {
    parts.push(`${form.covered_surface} m² cub.`);
  } else if (form.total_surface != null && form.total_surface !== '') {
    parts.push(`${form.total_surface} m²`);
  }
  return parts.join(' · ');
}

/** True si hay al menos un atributo/columna con valor (para mostrar el bloque en share). */
export function hasUfAttributeDisplayData(form = {}) {
  for (const key of UF_ATTRIBUTE_COLUMN_KEYS) {
    const value = form[key];
    if (value === null || value === undefined || value === '') continue;
    // Los boolean false del form (default al mapear null) no cuentan como dato cargado.
    if (typeof value === 'boolean') {
      if (value === true) return true;
      continue;
    }
    return true;
  }
  const attrs = form.attributes;
  if (!attrs || typeof attrs !== 'object') return false;
  return Object.values(attrs).some((value) => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'boolean') return value === true;
    return true;
  });
}

export function isUfAttributeFieldEmpty(value) {
  return value === null || value === undefined || value === '';
}
